import format from 'string-format';

/**
 * @private
 * @type {function} getBestLocale
 * @param {string} key - The key of the message template we wish to find
 * @param {string} locale - The preferred locale for said key.  should be in 'language' or 'language-territory' format
 * @param {object} stringsTable - The full dictionary of all localisable strings in the system
 * @returns {string} - A best fit locale for given key in in the following preference order:
 * 	locale-territory
 * 	locale
 * 	en
 */
export function getBestLocale(key, locale, stringsTable) {
	const bestLocale = [locale, locale.substring(0, 2), 'en'].find(locale => {
		const workingLocale = locale.toLowerCase();
		return stringsTable[workingLocale] && stringsTable[workingLocale].messageTemplates && stringsTable[workingLocale].messageTemplates[key];
	});

	if (!bestLocale) {
		throw (new Error(`Could not find any string lookup for ${key}`));
	}

	return bestLocale.toLowerCase();
}

/**
 * @private
 * @type {function} - getTemplateForLocale
 * @param {string} key - The key of the message template we wish to find
 * @param {string} locale - The preferred locale for said key.  should be in 'language' or 'language-territory' format
 * @param {object} stringsTable - The full dictionary of all localisable strings in the system
 * @returns {string} The template string for a chosen locale according to getBestLocale
 */
export function getTemplateForLocale(key, locale, stringsTable) {
	return stringsTable[getBestLocale(key, locale, stringsTable)].messageTemplates[key];
}

/**
 * @private
 * @type {function} getTemplateForLocale
 * @param {string} key - The key of the message template we wish to find
 * @param {object} values - A dictionary of values to interpolate into the string
 * @param {string} locale The preferred locale for said key.  should be in 'language' or 'language-territory' format
 * @param {object} stringsTable - The full dictionary of all localisable strings in the system
 * @returns {string} A formatted message string
 */
export function formatMessageInternal(stringsTable, key, locale, values) {
	const formatter = !!Object.prototype.toLocaleString ? x=>x.toLocaleString() : x=>x.toString();
	const template = getTemplateForLocale(key, locale, stringsTable);

	if (!values){
		return template;
	}

	const localeSpecificValues = {};
	for (const k in values) {
		localeSpecificValues[k] = formatter(values[k]);
	}

	return format(template, localeSpecificValues);
}

/**
 * @public
 * @type {function} getTemplateForLocale
 * @param {Object} stringsTable - Hashmap of all local string tables
 * @param {string} key - The key of the message template we wish to find
 * @param {object} values - A dictionary of values to interpolate into the string
 * @param {string} locale The preferred locale for said key.  should be in 'language' or 'language-territory' format
 * @returns {string} A formatted message string
 */
export function formatMessage(stringsTable, key, locale, values = null) {
	return formatMessageInternal(stringsTable, key, locale, values);
}

/**
 * @public
 * @type {function} currencySymbol
 * @param {string} currencyCode - Currency code to find currency symbol for
 * @returns {string} currency symbol
 *
 */
export function currencySymbol(currencyCode) {
	switch (currencyCode) {
	case 'AED':
		return 'د.إ';
	case 'AUD':
		return '$';
	case 'CAD':
		return '$';
	case 'EUR':
		return '€';
	case 'GBP':
		return '£';
	case 'HKD':
		return '$';
	case 'SGD':
		return '$';
	case 'USD':
		return '$';
	case 'ZAR':
		return 'R';
	default:
		return '£';
	}
}



// WEBPACK FOOTER //
// ../~/dna-react-intl/components/intl/localiser.js